import { useTranslation } from 'react-i18next';
import { useMemo, useState, Fragment, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { Routes } from 'config';
import { JourneyStage } from 'types';
import { useAppSelector } from 'store';
import { useIsDesktopDown } from 'hooks';
import { useCourseContext } from 'context';
import { codeNameToJourneyStep } from 'utils';
import { selectIsGuestUser } from 'store/slices/auth';

import { ResetWaiver } from '../reset-waiver';
import { LogoutButton } from '../logout-button';

import { StepButton } from './step-button';
import { BurgerButton } from './burger-button';
import { UserDropdown } from './user-dropdown';
import { NavigationLink } from './navigation-link';
import { ResponsiveMenu } from './responsive-menu';
import { LanguageButton } from './language-button';
import { NavLinkItem, TopNavigationProps } from './types';

const userLinks = [
  {
    label: 'Manage Account',
    i18nKey: 'manageAccount',
    href: Routes.Profile.Home,
  },
  {
    label: 'Family Information',
    i18nKey: 'familyInformation',
    href: Routes.FamilyInformation.Home,
  },
];

export const TopNavigation = ({ logoUrl }: TopNavigationProps) => {
  const { hasCourse, showWelcome, internalSteps } = useCourseContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isGuestUser = useAppSelector(selectIsGuestUser);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isResetDialogOpened, setResetDialogOpen] = useState(false);
  const isDesktopDown = useIsDesktopDown();
  const { t } = useTranslation(['header', 'common']);

  const onSuccess = () => {
    navigate(Routes.Dashboard.Home);
  };

  const links = useMemo(() => {
    const links: NavLinkItem[] = [
      {
        label: 'Dashboard',
        i18nKey: 'dashboard',
        href: Routes.Dashboard.Home,
      },
      {
        label: 'Resources',
        i18nKey: 'resources',
        href: Routes.Resources.Home,
      },
      {
        label: 'Contact Us',
        i18nKey: 'contactUs',
        href: Routes.ContactUs.Home,
      },
    ];

    if (hasCourse && !showWelcome) {
      links.push({
        asButton: true,
        label: 'Start New Waiver',
        i18nKey: 'startNewWaiver',
      });
    }

    return links;
  }, [showWelcome, hasCourse]);

  useEffect(() => {
    if (!isDesktopDown) {
      setIsMobileMenuOpen(false);
    }
  }, [isDesktopDown]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLinkClick = (asButton?: boolean) => {
    setIsMobileMenuOpen(false);
    if (asButton) {
      setResetDialogOpen(true);
    }
  };

  return (
    <>
      <div className="relative z-[5] flex h-12 w-full items-center justify-between md:h-16">
        <div className="flex items-center">
          <div className="h-6 md:h-8">
            {logoUrl && (
              <Link to={Routes.Dashboard.Home}>
                <img
                  alt="logo"
                  src={logoUrl}
                  className="h-full w-auto object-contain"
                />
              </Link>
            )}
          </div>
          <div className="hidden flex-1 pl-8 xl:flex">
            {links.map((link) => (
              <NavigationLink
                key={link.label}
                href={link.href}
                variant="desktop"
                label={t(link.i18nKey)}
                asButton={link.asButton}
                isActive={pathname === link.href}
                onClick={() => handleLinkClick(link.asButton)}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center">
          {!showWelcome && (
            <div className="flex items-center">
              {internalSteps?.map((item, index) => (
                <Fragment key={item.presentStep.id}>
                  {index !== 0 && (
                    <div className="bg-white-primary h-px min-w-2 md:min-w-3.5" />
                  )}
                  <StepButton
                    item={item.presentStep}
                    onClick={() => {
                      const { id, codeName, resourceVersions } =
                        item.presentStep;
                      const type = codeNameToJourneyStep(codeName);
                      if (type === JourneyStage.VACCINE) {
                        navigate(Routes.Course.Vaccine);
                      } else {
                        const isResource =
                          resourceVersions && resourceVersions.length > 0;
                        if (isResource) {
                          navigate(Routes.Course.Resource.replace(':id', id));
                        } else {
                          navigate(Routes.Course.Form.replace(':id', id));
                        }
                      }
                    }}
                  />
                </Fragment>
              ))}
            </div>
          )}
          <LanguageButton className="ml-6 hidden xl:flex" />
          {isGuestUser ? (
            <LogoutButton className="ml-10 hidden xl:flex" />
          ) : (
            <UserDropdown showLogoutButton links={userLinks} />
          )}
          <BurgerButton
            isOpen={isMobileMenuOpen}
            className="ml-3 xl:hidden"
            onClick={handleMobileMenuToggle}
          />
        </div>
      </div>
      {isMobileMenuOpen && (
        <ResponsiveMenu
          links={links}
          pathname={pathname}
          userLinks={userLinks}
          isGuestUser={isGuestUser}
          onClick={handleLinkClick}
        />
      )}

      <ResetWaiver
        onSuccess={onSuccess}
        open={isResetDialogOpened}
        onClose={() => setResetDialogOpen(false)}
      >
        <p>{t('resetWaiverText1')}</p>
        <p>{t('resetWaiverText2')}</p>
      </ResetWaiver>
    </>
  );
};
